import { css } from '@emotion/css';
import * as d3 from 'd3';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { Color } from 'src/styles';

export const gridStyle = css`
  fill: none;
  stroke: rgba(189, 189, 189);
  stroke-width: 1px;
  path {
    stroke-dasharray: 2 2;
    opacity: 0.3;
  }
  line {
    stroke-dasharray: 2 2;
    opacity: 0.3;
  }
  text {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 100;
    font-size: 11px;
    line-height: 11px;
    stroke-dasharray: none;
    stroke: ${Color.GRAYSCALE.GRAY8};
  }
`;

interface ChartOptions {
  xAxis?: {
    tickInterval?: d3.CountableTimeInterval;
    tickStep?: number;
    tickFormat?: string;
  };
}

export const drawChart = (
  element: HTMLElement,
  data: [number, number][], // [timestamp, price]
  width: number,
  height: number,
  options?: ChartOptions,
) => {
  d3.select(element).select('svg').remove();
  d3.select(element).select('.tooltip').remove();

  if (isEmpty(data)) {
    // Create SVG for "No data" message
    const svg = d3.select(element).append('svg').attr('width', width).attr('height', height);

    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', height / 2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .style('font-family', 'Inter')
      .style('font-size', '14px')
      .style('fill', Color.GRAYSCALE.GRAY8)
      .text('No data available');

    return;
  }

  const sortedData = [...data].sort((a, b) => a[0] - b[0]);

  const margin = { top: 16, right: 20, bottom: 60, left: 55 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  // Get min/max values
  const yMaxValue = d3.max(sortedData, d => d[1]) as number;
  const yMinValue = d3.min(sortedData, d => d[1]) as number;
  const xMinValue = d3.min(sortedData, d => d[0]) as number;
  const xMaxValue = d3.max(sortedData, d => d[0]) as number;

  // Add 5% padding to Y axis for better visualization
  const yPadding = (yMaxValue - yMinValue) * 0.05;
  const yMin = yMinValue - yPadding;
  const yMax = yMaxValue + yPadding;

  // Remove old svg and tooltip
  d3.select(element).select('svg').remove();
  d3.select(element).select('.tooltip').remove();

  d3.select(element).style('position', 'relative');

  // Create SVG
  const svg = d3
    .select(element)
    .append('svg')
    .attr('width', chartWidth + margin.left + margin.right)
    .attr('height', chartHeight + margin.top + margin.bottom)
    .append('g')
    .attr('transform', `translate(${margin.left}, ${margin.top})`);

  // Create scales
  const xScale = d3
    .scaleTime()
    .domain([new Date(xMinValue), new Date(xMaxValue)])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear().domain([yMin, yMax]).range([chartHeight, 0]);

  const tickConfig = {
    interval: options?.xAxis?.tickInterval?.every(options?.xAxis?.tickStep || 1),
    format: options?.xAxis?.tickFormat || 'HH:mm',
  };

  // X-axis grid
  svg
    .append('g')
    .attr('class', gridStyle)
    .attr('transform', `translate(0,${chartHeight})`)
    .call(
      d3
        .axisBottom(xScale)
        .ticks(options?.xAxis?.tickInterval?.every(options?.xAxis?.tickStep || 1) || tickConfig.interval)
        .tickSize(-chartHeight)
        .tickPadding(10)
        .tickFormat(value => moment(value as Date).format(options?.xAxis?.tickFormat || tickConfig.format)),
    );

  // Y-axis grid
  svg
    .append('g')
    .attr('class', gridStyle)
    .call(
      d3
        .axisLeft(yScale)
        .ticks(6)
        .tickSize(-chartWidth)
        .tickPadding(10)
        .tickFormat(d => `$${Number(d).toFixed(2)}`),
    );

  // Create gradient
  const defs = svg.append('defs');
  const gradient = defs
    .append('linearGradient')
    .attr('id', 'svgGradient')
    .attr('x1', '0%')
    .attr('x2', '0%')
    .attr('y1', '0%')
    .attr('y2', '100%');

  gradient.append('stop').attr('offset', '0%').attr('stop-color', 'rgba(2, 85, 163, 0.47)');

  gradient.append('stop').attr('offset', '100%').attr('stop-color', 'rgba(255, 255, 255, 0)');

  // Create area generator
  const area = d3
    .area<[number, number]>()
    .x(d => xScale(new Date(d[0])))
    .y0(chartHeight)
    .y1(d => yScale(d[1]))
    .curve(d3.curveMonotoneX);

  // Create line generator
  const line = d3
    .line<[number, number]>()
    .x(d => xScale(new Date(d[0])))
    .y(d => yScale(d[1]))
    .curve(d3.curveMonotoneX);

  // Draw area
  svg.append('path').datum(sortedData).attr('fill', 'url(#svgGradient)').attr('class', 'area').attr('d', area);

  // Draw line
  svg
    .append('path')
    .datum(sortedData)
    .attr('fill', 'none')
    .attr('stroke', '#0255A3')
    .attr('stroke-width', 2)
    .attr('class', 'line')
    .attr('d', line);

  // Create tooltip
  const tooltip = d3
    .select(element)
    .append('div')
    .attr('class', 'tooltip')
    .style('position', 'absolute')
    .style('visibility', 'hidden')
    .style('background-color', 'black')
    .style('color', 'white')
    .style('border', '1px solid #ddd')
    .style('border-radius', '4px')
    .style('padding', '8px')
    .style('font-size', '12px')
    .style('pointer-events', 'none')
    .style('z-index', '1000')
    .style('box-shadow', '0 2px 4px rgba(0,0,0,0.1)');

  const bisect = d3.bisector((d: [number, number]) => d[0]).left;

  const tooltipArea = svg
    .append('rect')
    .attr('width', chartWidth)
    .attr('height', chartHeight)
    .style('fill', 'none')
    .style('pointer-events', 'all');

  const focusCircle = svg
    .append('circle')
    .attr('r', 5)
    .attr('fill', '#0255A3')
    .attr('stroke', 'white')
    .attr('stroke-width', 2)
    .style('opacity', 0);

  tooltipArea
    .on('mouseover', () => {
      tooltip.style('visibility', 'visible');
      focusCircle.style('opacity', 1);
    })
    .on('mousemove', function (event) {
      const [mouseX] = d3.pointer(event, this);

      const x0 = +xScale.invert(mouseX);
      // Find closest data point
      const index = bisect(sortedData, x0);

      let d: [number, number];

      if (index === 0) {
        d = sortedData[0];
      } else if (index >= sortedData.length) {
        d = sortedData[sortedData.length - 1];
      } else {
        const d0 = sortedData[index - 1];
        const d1 = sortedData[index];
        d = x0 - d0[0] > d1[0] - x0 ? d1 : d0;
      }

      // Position focus circle
      focusCircle.attr('cx', xScale(new Date(d[0]))).attr('cy', yScale(d[1]));

      // Update tooltip content
      tooltip.html(`
      <div><strong>Date:</strong> ${moment(d[0]).format('MMM D, YYYY HH:mm')}</div>
      <div><strong>Price:</strong> $${d[1].toFixed(2)}</div>
    `);

      // Position tooltip
      tooltip.style('top', `${event.offsetY - 30}px`).style('left', `${event.offsetX + 10}px`);
    })
    .on('mouseout', () => {
      tooltip.style('visibility', 'hidden');
      focusCircle.style('opacity', 0);
    });
};
